import { useCallback, useId } from "react";
import {
  alpha,
  Box,
  Button,
  Chip,
  DialogActions,
  DialogContent,
  Paper,
  Stack,
  Typography,
} from "@mui/material";
import CampaignIcon from "@mui/icons-material/Campaign";
import type { NewsItem } from "@/features/news/newsItems";
import { DialogShell } from "@/shared/ui/DialogShell";

interface NewsModalProps {
  open: boolean;
  onClose: () => void;
  onExited?: () => void;
  newsItems: readonly NewsItem[];
}

export function NewsModal({
  open,
  onClose,
  onExited,
  newsItems,
}: NewsModalProps) {
  const id = useId();
  const titleId = `${id}-title`;
  const descriptionId = `${id}-description`;
  const focusTitle = useCallback((node: HTMLHeadingElement | null) => {
    node?.focus({ preventScroll: true });
  }, []);

  return (
    <DialogShell
      open={open}
      onClose={onClose}
      onExited={onExited}
      size="sm"
      titleId={titleId}
      descriptionId={descriptionId}
      paperSx={{
        overflow: "hidden",
        m: { xs: 2, sm: 4 },
        width: { xs: "calc(100% - 32px)", sm: "calc(100% - 64px)" },
        maxHeight: { xs: "calc(100% - 32px)", sm: "calc(100% - 64px)" },
      }}
    >
      <DialogContent sx={{ pt: 4, pb: 1, px: { xs: 2, sm: 4 } }}>
        <Box sx={{ textAlign: "center", mb: 3 }}>
          <Box
            sx={(theme) => ({
              width: 52,
              height: 52,
              borderRadius: "50%",
              backgroundColor: "primary.main",
              color: "primary.contrastText",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              mx: "auto",
              mb: 2,
              boxShadow: `0 4px 14px ${alpha(theme.palette.primary.main, 0.25)}`,
            })}
          >
            <CampaignIcon sx={{ fontSize: 26 }} />
          </Box>
          <Typography
            id={titleId}
            variant="h5"
            component="h2"
            tabIndex={-1}
            ref={focusTitle}
            sx={{ fontWeight: 700, mb: 0.5 }}
          >
            What's New
          </Typography>
          <Typography id={descriptionId} variant="body2" color="text.secondary">
            Updates and tips for Investigator.
          </Typography>
        </Box>

        <Stack spacing={2}>
          {newsItems.length === 0 && (
            <Typography
              sx={{ py: 3, textAlign: "center" }}
              color="text.secondary"
            >
              You're all caught up! Check back later for new updates.
            </Typography>
          )}
          {newsItems.map((item) => (
            <Paper
              key={item.newsId}
              component="article"
              elevation={0}
              sx={(theme) => ({
                p: { xs: 2, sm: 2.5 },
                borderRadius: 2.5,
                border: "1px solid",
                borderColor: "divider",
                backgroundColor: alpha(theme.palette.text.primary, 0.025),
                overflowWrap: "anywhere",
              })}
            >
              <Chip
                label={item.steps?.length ? "Quick start" : "Update"}
                size="small"
                sx={(theme) => ({
                  mb: 1.5,
                  height: 26,
                  fontSize: "0.72rem",
                  fontWeight: 600,
                  borderRadius: 2,
                  color: "primary.main",
                  backgroundColor: alpha(theme.palette.primary.main, 0.12),
                })}
              />
              <Typography
                variant="subtitle1"
                component="h3"
                sx={{ fontWeight: 700, lineHeight: 1.4, mb: 1 }}
              >
                {item.title}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {item.summary}
              </Typography>

              {!!item.steps?.length && (
                <Stack
                  component="ol"
                  role="list"
                  spacing={2}
                  sx={{ listStyle: "none", p: 0, mt: 2.5, mb: 0 }}
                >
                  {item.steps.map((step, index) => (
                    <Box
                      key={step.title}
                      component="li"
                      sx={{ display: "flex", gap: 1.5 }}
                    >
                      <Box
                        sx={(theme) => ({
                          flexShrink: 0,
                          width: 26,
                          height: 26,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          borderRadius: "50%",
                          backgroundColor: alpha(
                            theme.palette.primary.main,
                            0.1
                          ),
                          color: "primary.main",
                          fontSize: "0.75rem",
                          fontWeight: 700,
                        })}
                      >
                        {index + 1}
                      </Box>
                      <Box sx={{ minWidth: 0 }}>
                        <Typography
                          variant="body2"
                          component="h4"
                          sx={{ fontWeight: 600, mb: 0.25 }}
                        >
                          {step.title}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {step.description}
                        </Typography>
                      </Box>
                    </Box>
                  ))}
                </Stack>
              )}

              {item.tip && (
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{
                    mt: 2.5,
                    pt: 2,
                    borderTop: "1px solid",
                    borderColor: "divider",
                  }}
                >
                  <Box component="strong" sx={{ color: "text.primary" }}>
                    Tip:{" "}
                  </Box>
                  {item.tip}
                </Typography>
              )}
            </Paper>
          ))}
        </Stack>
      </DialogContent>

      <DialogActions
        sx={{
          px: 4,
          pb: 3,
          pt: 2,
          justifyContent: "center",
          borderTop: "1px solid",
          borderColor: "divider",
        }}
      >
        <Button
          onClick={onClose}
          variant="gradient"
          disableElevation
          sx={{ px: 5, py: 1, textTransform: "none", fontWeight: 600 }}
        >
          Got it
        </Button>
      </DialogActions>
    </DialogShell>
  );
}
